import type {
  RecurringTodo,
  RecurringTodoRun,
  CreateRecurringTodoRequest,
  UpdateRecurringTodoRequest,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export async function fetchRecurringTodos(signal?: AbortSignal): Promise<RecurringTodo[]> {
  const response = await fetch(`${API_BASE_URL}/recurring-todos`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch recurring to-dos: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

export async function createRecurringTodo(
  payload: CreateRecurringTodoRequest,
  signal?: AbortSignal,
): Promise<RecurringTodo> {
  const response = await fetch(`${API_BASE_URL}/recurring-todos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to create recurring to-do: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }
  return response.json();
}

export async function updateRecurringTodo(
  id: string,
  payload: UpdateRecurringTodoRequest,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/recurring-todos/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to update recurring to-do: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }
}

/**
 * Run a recurring definition immediately (Run Now). When `advance` is true the
 * schedule moves to the next occurrence as a scheduled run would; when false
 * the run is an ad-hoc extra and the schedule is left untouched. Returns the
 * refreshed definition.
 */
export async function runRecurringTodo(
  id: string,
  advance: boolean,
  signal?: AbortSignal,
): Promise<RecurringTodo> {
  const response = await fetch(`${API_BASE_URL}/recurring-todos/${encodeURIComponent(id)}/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ advance }),
    signal,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to run recurring task: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }
  return response.json();
}

/** Fetch the run history for a recurring definition, most recent first. */
export async function fetchRecurringTodoRuns(
  id: string,
  signal?: AbortSignal,
): Promise<RecurringTodoRun[]> {
  const response = await fetch(`${API_BASE_URL}/recurring-todos/${encodeURIComponent(id)}/runs`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch run history: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

export async function deleteRecurringTodo(id: string, signal?: AbortSignal): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/recurring-todos/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    signal,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: `Failed to delete recurring to-do: ${response.status}`,
    }));
    throw new Error(errorData.error);
  }
}
